import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { SafetyRoundTemplateStatus, SafetyRoundTemplateSummary } from '../safety-round.models';
import { FlexiSafetyRoundTemplateService } from './safety-round-template.service';

/** One status chip above the table. `null` is "All". */
interface StatusFilter {
  value: SafetyRoundTemplateStatus | null;
  labelKey: string;
}

/**
 * Admin mode › Flexi › Settings › Vernerundemaler.
 *
 * Every template of the application, whatever its status, is loaded once and
 * filtered here, so the chips can show their counts. The backend scopes the
 * list to the admin's application.
 */
@Component({
  selector: 'flexi-safety-round-template-library',
  templateUrl: './safety-round-template-library.component.html',
  styleUrls: ['./safety-round-template-library.component.scss'],
  standalone: false,
})
export class SafetyRoundTemplateLibraryComponent implements OnInit, OnDestroy {
  readonly TemplateStatus = SafetyRoundTemplateStatus;

  readonly filters: StatusFilter[] = [
    { value: SafetyRoundTemplateStatus.Published, labelKey: 'FLEXI_SAFETY_ROUNDS.TEMPLATE_STATUS.1' },
    { value: SafetyRoundTemplateStatus.Draft, labelKey: 'FLEXI_SAFETY_ROUNDS.TEMPLATE_STATUS.0' },
    { value: SafetyRoundTemplateStatus.Archived, labelKey: 'FLEXI_SAFETY_ROUNDS.TEMPLATE_STATUS.2' },
    { value: null, labelKey: 'FLEXI_SAFETY_ROUNDS.LIBRARY.FILTER_ALL' },
  ];

  templates: SafetyRoundTemplateSummary[] = [];
  visible: SafetyRoundTemplateSummary[] = [];
  loading = true;
  loadFailed = false;
  status: SafetyRoundTemplateStatus | null = SafetyRoundTemplateStatus.Published;
  search = '';
  /** Templates whose default switch is being saved. */
  busyIds = new Set<number>();

  /* ---- "New template" dialog ---- */
  createOpen = false;
  newName = '';
  newIndustry = '';
  newDescription = '';
  creating = false;

  /** The template whose assign drawer is open. */
  assigning: SafetyRoundTemplateSummary | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: FlexiSafetyRoundTemplateService,
    private router: Router,
    private route: ActivatedRoute,
    private notifications: NotificationService,
  ) {}

  /** Published templates new businesses receive. */
  get defaultsForNew(): SafetyRoundTemplateSummary[] {
    return this.templates.filter(t => t.IsDefaultForNewBusinesses && t.Status === SafetyRoundTemplateStatus.Published);
  }

  get defaultsForNewNames(): string {
    return this.defaultsForNew.map(t => t.Name).join(', ');
  }

  get drafts(): SafetyRoundTemplateSummary[] {
    return this.templates.filter(t => t.Status === SafetyRoundTemplateStatus.Draft);
  }

  get draftNames(): string {
    return this.drafts.map(t => t.Name).join(', ');
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.api
      .list()
      .pipe(takeUntil(this.destroy$), finalize(() => (this.loading = false)))
      .subscribe({
        next: rows => {
          this.templates = rows;
          this.applyFilter();
        },
        error: () => {
          this.templates = [];
          this.visible = [];
          this.loadFailed = true;
        },
      });
  }

  count(status: SafetyRoundTemplateStatus | null): number {
    return status === null ? this.templates.length : this.templates.filter(t => t.Status === status).length;
  }

  pickStatus(status: SafetyRoundTemplateStatus | null): void {
    this.status = status;
    this.applyFilter();
  }

  /** Rebuilt rather than derived in a getter, so p-table keeps its paginator. */
  applyFilter(): void {
    const needle = this.search.trim().toLowerCase();
    this.visible = this.templates.filter(t =>
      (this.status === null || t.Status === this.status)
      && (!needle || t.Name.toLowerCase().includes(needle) || (t.Industry ?? '').toLowerCase().includes(needle)));
  }

  open(template: SafetyRoundTemplateSummary): void {
    this.router.navigate([template.Id], { relativeTo: this.route });
  }

  statusKey(status: SafetyRoundTemplateStatus): string {
    return `FLEXI_SAFETY_ROUNDS.TEMPLATE_STATUS.${status}`;
  }

  statusClass(status: SafetyRoundTemplateStatus): string {
    switch (status) {
      case SafetyRoundTemplateStatus.Published:
        return 'fvr-pill--done';
      case SafetyRoundTemplateStatus.Draft:
        return 'fvr-pill--draft';
      default:
        return '';
    }
  }

  isPublished(template: SafetyRoundTemplateSummary): boolean {
    return template.Status === SafetyRoundTemplateStatus.Published;
  }

  /** Optimistic; the switch flips back if the save fails. */
  toggleDefault(template: SafetyRoundTemplateSummary): void {
    if (!this.isPublished(template) || this.busyIds.has(template.Id)) {
      return;
    }
    const next = !template.IsDefaultForNewBusinesses;
    template.IsDefaultForNewBusinesses = next;
    this.busyIds.add(template.Id);
    this.api
      .setDefaultForNew(template.Id, next)
      .pipe(takeUntil(this.destroy$), finalize(() => this.busyIds.delete(template.Id)))
      .subscribe({
        next: () => this.notifications.success('UI_NOTIFICATIONS.SAVED'),
        error: () => (template.IsDefaultForNewBusinesses = !next),
      });
  }

  openAssign(template: SafetyRoundTemplateSummary): void {
    this.assigning = template;
  }

  assignClosed(saved: boolean): void {
    this.assigning = null;
    if (saved) {
      this.load();
    }
  }

  /* ---- New template ---- */

  openCreate(): void {
    this.newName = '';
    this.newIndustry = '';
    this.newDescription = '';
    this.createOpen = true;
  }

  closeCreate(): void {
    if (!this.creating) {
      this.createOpen = false;
    }
  }

  create(): void {
    const name = this.newName.trim();
    if (!name || this.creating) {
      return;
    }
    this.creating = true;
    this.api
      .create({
        Name: name,
        Industry: this.newIndustry.trim() || null,
        Description: this.newDescription.trim() || null,
      })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.creating = false)))
      .subscribe(id => {
        this.createOpen = false;
        this.router.navigate([id], { relativeTo: this.route });
      });
  }
}
